package com.fantasytenis.web.theme

import androidx.compose.runtime.*
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.rgb

/**
 * Theme mode enum for light/dark theme support.
 */
enum class ThemeMode {
    LIGHT,
    DARK,
    SYSTEM
}

/**
 * Theme state manager singleton.
 * Handles theme persistence and system theme detection.
 */
object ThemeState {
    private const val STORAGE_KEY = "fantasy-tennis-theme"

    private var _themeMode = mutableStateOf(loadSavedTheme())
    val themeMode: State<ThemeMode> = _themeMode

    private var _isDarkMode = mutableStateOf(calculateIsDark())
    val isDarkMode: State<Boolean> = _isDarkMode

    init {
        // Listen for system theme changes
        setupSystemThemeListener()
    }

    fun setTheme(mode: ThemeMode) {
        _themeMode.value = mode
        _isDarkMode.value = calculateIsDark()
        saveTheme(mode)
    }

    fun toggleTheme() {
        val newMode = if (_isDarkMode.value) ThemeMode.LIGHT else ThemeMode.DARK
        setTheme(newMode)
    }

    private fun loadSavedTheme(): ThemeMode {
        return try {
            val saved = localStorage.getItem(STORAGE_KEY)
            when (saved) {
                "LIGHT" -> ThemeMode.LIGHT
                "DARK" -> ThemeMode.DARK
                "SYSTEM" -> ThemeMode.SYSTEM
                else -> ThemeMode.SYSTEM
            }
        } catch (e: Exception) {
            ThemeMode.SYSTEM
        }
    }

    private fun saveTheme(mode: ThemeMode) {
        try {
            localStorage.setItem(STORAGE_KEY, mode.name)
        } catch (e: Exception) {
            // Ignore storage errors
        }
    }

    private fun calculateIsDark(): Boolean {
        return when (_themeMode.value) {
            ThemeMode.LIGHT -> false
            ThemeMode.DARK -> true
            ThemeMode.SYSTEM -> isSystemDarkMode()
        }
    }

    private fun isSystemDarkMode(): Boolean {
        return try {
            window.matchMedia("(prefers-color-scheme: dark)").matches
        } catch (e: Exception) {
            false
        }
    }

    private fun setupSystemThemeListener() {
        try {
            val mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
            mediaQuery.addEventListener("change", { _: org.w3c.dom.events.Event ->
                if (_themeMode.value == ThemeMode.SYSTEM) {
                    _isDarkMode.value = calculateIsDark()
                }
            })
        } catch (e: Exception) {
            // Ignore if matchMedia not supported
        }
    }
}

/**
 * Theme-aware color palette that switches based on dark mode.
 * Use Theme.colors instead of Colors directly for theme support.
 */
object Theme {
    /**
     * Get the current theme colors based on dark mode state.
     */
    val colors: ThemeColors
        @Composable
        get() {
            val isDark by ThemeState.isDarkMode
            return if (isDark) DarkColors else LightColors
        }
}

/**
 * Interface for theme colors to ensure both light and dark have same properties.
 */
interface ThemeColors {
    // Primary colors
    val primary: CSSColorValue
    val primaryLight: CSSColorValue
    val primaryDark: CSSColorValue

    // Secondary colors
    val secondary: CSSColorValue
    val secondaryLight: CSSColorValue
    val secondaryDark: CSSColorValue

    // Accent colors
    val accent: CSSColorValue
    val accentLight: CSSColorValue
    val accentDark: CSSColorValue

    // Background colors
    val background: CSSColorValue
    val surface: CSSColorValue
    val surfaceVariant: CSSColorValue

    // Text colors
    val textPrimary: CSSColorValue
    val textSecondary: CSSColorValue
    val textDisabled: CSSColorValue
    val textOnPrimary: CSSColorValue

    // Border colors
    val border: CSSColorValue
    val borderLight: CSSColorValue

    // Semantic colors
    val success: CSSColorValue
    val successLight: CSSColorValue
    val successDark: CSSColorValue
    val warning: CSSColorValue
    val warningLight: CSSColorValue
    val warningDark: CSSColorValue
    val error: CSSColorValue
    val errorLight: CSSColorValue
    val errorDark: CSSColorValue
    val info: CSSColorValue
    val infoLight: CSSColorValue
    val infoDark: CSSColorValue

    // Highlight colors
    val highlightBackground: CSSColorValue
    val highlightText: CSSColorValue

    // Progress colors
    val progressNormal: CSSColorValue
    val progressWarning: CSSColorValue
    val progressDanger: CSSColorValue
    val progressBackground: CSSColorValue
}

/**
 * Light theme color implementation.
 */
object LightColors : ThemeColors {
    override val primary = rgb(0, 166, 81)
    override val primaryLight = rgb(76, 196, 133)
    override val primaryDark = rgb(0, 128, 63)

    override val secondary = rgb(255, 215, 0)
    override val secondaryLight = rgb(255, 235, 102)
    override val secondaryDark = rgb(204, 172, 0)

    override val accent = rgb(33, 150, 243)
    override val accentLight = rgb(100, 181, 246)
    override val accentDark = rgb(25, 118, 210)

    override val background = rgb(250, 250, 250)
    override val surface = rgb(255, 255, 255)
    override val surfaceVariant = rgb(245, 245, 245)

    override val textPrimary = rgb(33, 33, 33)
    override val textSecondary = rgb(117, 117, 117)
    override val textDisabled = rgb(189, 189, 189)
    override val textOnPrimary = rgb(255, 255, 255)

    override val border = rgb(224, 224, 224)
    override val borderLight = rgb(240, 240, 240)

    override val success = rgb(76, 175, 80)
    override val successLight = rgb(232, 245, 233)
    override val successDark = rgb(56, 142, 60)
    override val warning = rgb(255, 152, 0)
    override val warningLight = rgb(255, 243, 224)
    override val warningDark = rgb(245, 124, 0)
    override val error = rgb(244, 67, 54)
    override val errorLight = rgb(255, 235, 238)
    override val errorDark = rgb(211, 47, 47)
    override val info = rgb(33, 150, 243)
    override val infoLight = rgb(227, 242, 253)
    override val infoDark = rgb(25, 118, 210)

    override val highlightBackground = rgb(227, 242, 253)
    override val highlightText = rgb(25, 118, 210)

    override val progressNormal = primary
    override val progressWarning = rgb(255, 152, 0)
    override val progressDanger = rgb(244, 67, 54)
    override val progressBackground = rgb(224, 224, 224)
}

/**
 * Dark theme color implementation.
 */
object DarkColors : ThemeColors {
    override val primary = rgb(76, 196, 133) // Lighter green for dark mode
    override val primaryLight = rgb(129, 216, 168)
    override val primaryDark = rgb(0, 166, 81)

    override val secondary = rgb(255, 215, 0)
    override val secondaryLight = rgb(255, 235, 102)
    override val secondaryDark = rgb(204, 172, 0)

    override val accent = rgb(100, 181, 246) // Lighter blue for dark mode
    override val accentLight = rgb(144, 202, 249)
    override val accentDark = rgb(33, 150, 243)

    override val background = rgb(18, 18, 18) // #121212
    override val surface = rgb(30, 30, 30) // #1E1E1E
    override val surfaceVariant = rgb(45, 45, 45) // #2D2D2D

    override val textPrimary = rgb(255, 255, 255)
    override val textSecondary = rgb(179, 179, 179) // #B3B3B3
    override val textDisabled = rgb(102, 102, 102) // #666666
    override val textOnPrimary = rgb(0, 0, 0) // Black text on light green

    override val border = rgb(66, 66, 66) // #424242
    override val borderLight = rgb(55, 55, 55) // #373737

    override val success = rgb(129, 199, 132) // Lighter for dark mode
    override val successLight = rgb(46, 80, 48) // Darker background for light text
    override val successDark = rgb(76, 175, 80)
    override val warning = rgb(255, 183, 77) // Lighter for dark mode
    override val warningLight = rgb(78, 60, 26) // Darker background
    override val warningDark = rgb(255, 152, 0)
    override val error = rgb(239, 154, 154) // Lighter for dark mode
    override val errorLight = rgb(80, 40, 40) // Darker background
    override val errorDark = rgb(244, 67, 54)
    override val info = rgb(144, 202, 249) // Lighter for dark mode
    override val infoLight = rgb(30, 60, 90) // Darker background
    override val infoDark = rgb(33, 150, 243)

    override val highlightBackground = rgb(30, 60, 90) // Dark blue background
    override val highlightText = rgb(144, 202, 249) // Light blue text

    override val progressNormal = primary
    override val progressWarning = rgb(255, 183, 77)
    override val progressDanger = rgb(239, 154, 154)
    override val progressBackground = rgb(66, 66, 66)
}

/**
 * Composable to provide theme context.
 * Wraps content with theme-aware styling.
 */
@Composable
fun FantasyTennisTheme(
    content: @Composable () -> Unit
) {
    val isDark by ThemeState.isDarkMode

    // Apply body class for CSS-based theming
    LaunchedEffect(isDark) {
        val body = kotlinx.browser.document.body
        if (isDark) {
            body?.classList?.add("dark-theme")
            body?.classList?.remove("light-theme")
        } else {
            body?.classList?.add("light-theme")
            body?.classList?.remove("dark-theme")
        }
    }

    content()
}
